import React from 'react';

import styled from 'styled-components';

import CoreTooltip from 'shared/CoreTooltip';
import style from 'styles/styled-components/style';

interface IHorizontalBarGraph {
	data: Record<string, number>;
	_width?: string | number;
	unit?: string;
	max?: number;
	background?: boolean;
}

/** HorizontalBarGraph 수평 바 그래프
 * @param {Record<string, number>} data - Record<string, number>
 * @param {string | number} _width - width
 * @param {string} unit - 단위 default: %
 * @param {number} max - 바의 넓이의 max value, 주의! data의 최댓값이랑은 다름
 * @param {boolean} background - background에 색상을 넣을건지 입력받음 default: false
 **/
const HorizontalBarGraph = ({
	data,
	_width,
	unit = '%',
	max,
	background = false,
}: IHorizontalBarGraph): JSX.Element => {
	const max_data = Math.max(...Object.values(data)) || 100;
	const max_width = max ?? (Math.max(...Object.values(data)) || 100);
	return (
		<>
			<Grid
				flex="start"
				_gap="8px"
				_width={
					_width ? (typeof _width === 'number' ? `${_width}px` : _width) : undefined
				}
			>
				<Grid _width="auto" flex="start" direction="column" align_items="end" _gap="6px">
					{Object.keys(data).map((item, idx) => (
						<HorizontalLabel key={idx}>{item}</HorizontalLabel>
					))}
				</Grid>

				<Grid
					flex="start"
					direction="column"
					align_items="start"
					_gap="6px"
					_width={
						_width ? (typeof _width === 'number' ? `${_width}px` : _width) : undefined
					}
				>
					{Object.values(data).map((item, idx) => (
						<CoreTooltip
							key={idx}
							wrapper_width="100%"
							text={item + unit}
							direction="right"
						>
							<Grid flex="start">
								{background ? (
									<>
										<HorizontalBar
											_width={`${(item / max_width) * 100}%`}
											radius={item === max_width ? '4px' : '4px 0 0 4px'}
											color_type={
												item === max_data ? 'primary' : background ? 'gray30' : 'gray'
											}
										>
											{/* 크기의 10%이상일때만 숫자 보이기 */}
											{item === max_data &&
												item / max_width > 0.1 &&
												`${Math.round(item)}${unit}`}
										</HorizontalBar>
										<Grid _width={`${100 - (item / max_width) * 100}%`}>
											<HorizontalBar
												_width="100%"
												radius={item === 0 ? '4px' : '0 4px 4px 0'}
												color_type="gray"
											/>{' '}
										</Grid>
									</>
								) : (
									<HorizontalBar
										_width={`${(item / max_width) * 100}%`}
										radius={4}
										color_type={item === max_data ? 'primary' : 'gray'}
									>
										{item === max_data && `${Math.round(item)}${unit}`}
									</HorizontalBar>
								)}
							</Grid>
						</CoreTooltip>
					))}
				</Grid>
			</Grid>
		</>
	);
};

const { Grid, Text } = style;

export const HorizontalBar = styled.div<{
	_width: string | number;
	color_type: 'primary' | 'gray' | 'gray30';
	radius?: string | number;
}>`
	width: ${({ _width }) => (typeof _width === 'number' ? `${_width}px` : _width)};
	height: 20px;
	border-radius: ${({ radius }) =>
		radius ? (typeof radius === 'number' ? `${radius}px` : radius) : '0'};
	background-color: ${({ theme, color_type }) => {
		const color_mapper: Record<string, string> = {
			primary: theme.colors.uplus.primary50_key,
			gray: theme.colors.gray.gray15,
			gray30: theme.colors.gray.gray30,
		};

		return color_mapper[color_type];
	}};
	color: ${({ theme, color_type }) => {
		const color_mapper: Record<string, string> = {
			primary: theme.colors.gray.gray_white,
			gray: theme.colors.gray.gray80_text,
		};

		return color_mapper[color_type];
	}};

	${({ theme }) => theme.typo.body.span};
	${({ theme }) => theme.common.flexCenter};
`;

export const HorizontalLabel = styled.p<{ _width?: number | string }>`
	width: ${({ _width }) =>
		_width ? (typeof _width === 'number' ? `${_width}px` : _width) : 'auto'};
	${({ theme }) => theme.typo.body.span};
	text-align: right;
	white-space: nowrap;
`;

export default HorizontalBarGraph;
